import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { bffApiClient } from '@api/bff/client';

type MeasurementImageDialogProps = {
  open: boolean;
  runId: string;
  imageId: string;
  onClose: () => void;
};

const MeasurementImageDialog: React.FC<MeasurementImageDialogProps> = ({
  open,
  runId,
  imageId,
  onClose
}) => {
  const { t } = useTranslation('calibration');
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let objectUrl: string | null = null;
    let cancelled = false;

    const fetchImage = async () => {
      setIsLoading(true);
      try {
        const response = await bffApiClient.get(
          `/api/test-runs/${runId}/images/${imageId}`,
          { responseType: 'blob' }
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setImageSrc(objectUrl);
      } catch {
        if (!cancelled) setImageSrc(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, runId, imageId]);

  const handleClose = useCallback(() => {
    setImageSrc(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg">
      <DialogTitle>{t('runner.imageDialogTitle')}</DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && imageSrc && (
          <Box
            component="img"
            src={imageSrc}
            alt={t('runner.imageDialogTitle')}
            sx={{ maxWidth: '100%', display: 'block' }}
          />
        )}
        {!isLoading && !imageSrc && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            {t('runner.imageLoadFailed')}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('runner.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MeasurementImageDialog;
